import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productAPI } from '../../services/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await productAPI.getProduct(id);
      setProduct(response.data);
      setSelectedImage(0);
    } catch (error) {
      console.error('Failed to load product:', error);
      if (error.response?.status === 404) {
        setError('Product not found.');
      } else {
        setError('Failed to load product details. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  };

  const backButtonStyle = {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  };

  const productGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    marginTop: '20px'
  };

  const imagesSectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  };

  const mainImageStyle = {
    width: '100%',
    height: '400px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa'
  };

  const thumbnailContainerStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  };

  const thumbnailStyle = {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'border-color 0.2s'
  };

  const activeThumbnailStyle = {
    ...thumbnailStyle,
    borderColor: '#007bff'
  };

  const detailsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  };

  const priceStyle = {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#2c5aa0',
    margin: '10px 0'
  };

  const stockBadgeStyle = {
    display: 'inline-block',
    padding: '8px 16px',
    borderRadius: '16px',
    fontSize: '14px',
    fontWeight: 'bold'
  };

  const inStockStyle = {
    ...stockBadgeStyle,
    backgroundColor: '#d4edda',
    color: '#155724'
  };

  const outOfStockStyle = {
    ...stockBadgeStyle,
    backgroundColor: '#f8d7da',
    color: '#721c24'
  };

  const specTableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px'
  };

  const specRowStyle = {
    borderBottom: '1px solid #dee2e6'
  };

  const specLabelStyle = {
    padding: '12px 16px',
    fontWeight: 'bold',
    backgroundColor: '#f8f9fa',
    width: '30%',
    textAlign: 'left'
  };

  const specValueStyle = {
    padding: '12px 16px',
    textAlign: 'left'
  };

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>Loading product details...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <button
          onClick={() => navigate('/products')}
          style={backButtonStyle}
        >
          ← Back to Products
        </button>
        
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '8px'
        }}>
          <h2 style={{ marginBottom: '16px' }}>Error</h2>
          <p style={{ marginBottom: '20px' }}>{error}</p>
          <button
            onClick={loadProduct}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={containerStyle}>
        <p>Product not found.</p>
      </div>
    );
  }

  const images = ['https://placehold.co/400x400/f0f0f0/888888?text=Auto+Part+Image'];

  return (
    <div style={containerStyle}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/products')}
        style={backButtonStyle}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
      >
        ← Back to Products
      </button>

      {/* Product Details Grid */}
      <div style={productGridStyle}>
        {/* Images Section */}
        <div style={imagesSectionStyle}>
          {/* Main Image */}
          <img
            src={images[selectedImage]}
            alt={product.name}
            style={mainImageStyle}
          />
          
          {/* Thumbnails */}
          {images.length > 1 && (
            <div style={thumbnailContainerStyle}>
              {images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${product.name} view ${index + 1}`}
                  style={selectedImage === index ? activeThumbnailStyle : thumbnailStyle}
                  onClick={() => setSelectedImage(index)}
                  onMouseEnter={(e) => {
                    if (selectedImage !== index) {
                      e.target.style.borderColor = '#6c757d';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedImage !== index) {
                      e.target.style.borderColor = 'transparent';
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Information */}
        <div style={detailsStyle}>
          {/* Title and Category */}
          <div>
            <h1 style={{ 
              margin: '0 0 8px 0',
              fontSize: '28px',
              color: '#333',
              lineHeight: '1.3'
            }}>
              {product.name}
            </h1>
            <p style={{ 
              margin: '0 0 4px 0',
              fontSize: '16px',
              color: '#666',
              textTransform: 'capitalize'
            }}>
              Category: {product.category}
            </p>
            {product.brand && (
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                color: '#666',
                fontStyle: 'italic'
              }}>
                Brand: {product.brand}
              </p>
            )}
          </div>

          {/* Price */}
          <div style={priceStyle}>
            {formatPrice(product.price)}
          </div>

          {/* Stock Status */}
          <div>
            <span style={product.stockQuantity > 0 ? inStockStyle : outOfStockStyle}>
              {product.stockQuantity > 0 
                ? `${product.stockQuantity} in stock` 
                : 'Out of stock'
              }
            </span>
          </div>

          {/* Description */}
          {product.description && (
            <div>
              <h3 style={{ marginBottom: '12px', color: '#333' }}>Description</h3>
              <p style={{ 
                lineHeight: '1.6',
                color: '#555',
                fontSize: '16px',
                margin: 0
              }}>
                {product.description}
              </p>
            </div>
          )}

          {/* Product Specifications */}
          <div>
            <h3 style={{ marginBottom: '16px', color: '#333' }}>Specifications</h3>
            <table style={specTableStyle}>
              <tbody>
                {product.partNumber && (
                  <tr style={specRowStyle}>
                    <td style={specLabelStyle}>Part Number</td>
                    <td style={specValueStyle}>{product.partNumber}</td>
                  </tr>
                )}
                <tr style={specRowStyle}>
                  <td style={specLabelStyle}>Category</td>
                  <td style={specValueStyle}>{product.category}</td>
                </tr>
                {product.brand && (
                  <tr style={specRowStyle}>
                    <td style={specLabelStyle}>Brand</td>
                    <td style={specValueStyle}>{product.brand}</td>
                  </tr>
                )}
                <tr style={specRowStyle}>
                  <td style={specLabelStyle}>Stock Quantity</td>
                  <td style={specValueStyle}>{product.stockQuantity}</td>
                </tr>
                {product.createdAt && (
                  <tr style={specRowStyle}>
                    <td style={specLabelStyle}>Date Added</td>
                    <td style={specValueStyle}>{formatDate(product.createdAt)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;